import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import { Container, Row, RowColumn, Section } from 'chaoskit/src/components'
import { gradient } from 'chaoskit/src/assets/styles/utility'
import { MDXRenderer } from 'gatsby-plugin-mdx'

import {
  HomeHero,
  HomeHeroGrid,
  HomeHeroGridImage,
  HomeHeroGridContent,
} from '~partials/home/Hero'
import BaseLayout from '~layouts/BaseLayout'
import { HeroContent, HeroTitle, HeroTitleSub } from '~partials/Hero'
import Data from '~components/Data'
import { ReactComponent as HeroSVG } from '~media/aws/quote.svg'
import HelmetSEO from '~partials/global/HelmetSEO'
import FilteredContent from '~components/FilteredContent'
import TableOfContents from '~components/TableOfContents'

const AWSHero = ({ data }) => {
  const theme = useTheme()

  return (
    <HomeHero css={{ paddingBottom: 0, ...gradient.dark(theme) }}>
      <HeroContent>
        <HomeHeroGrid>
          <HomeHeroGridImage>
            <HeroSVG />
          </HomeHeroGridImage>
          <HomeHeroGridContent>
            <HeroTitle
              css={{
                ...theme.text['4xlarge__fluid'],
                fontWeight: theme.fontWeight.base,
              }}
            >
              <Data>{data.heading}</Data>
            </HeroTitle>
            <HeroTitleSub>
              <Data>{data.blurb}</Data>
            </HeroTitleSub>
          </HomeHeroGridContent>
        </HomeHeroGrid>
      </HeroContent>
    </HomeHero>
  )
}

AWSHero.propTypes = {
  data: PropTypes.object.isRequired,
}

const AWS = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData, body, headings, tableOfContents },
    },
  },
}) => {
  const theme = useTheme()

  return (
    <BaseLayout footerProps={{ hideCTA: true }}>
      <HelmetSEO
        title={pageData.title}
        description={pageData.description}
        image={pageData.socialMediaImage}
      />
      <AWSHero data={pageData.hero} />
      <Section>
        <Container>
          <Row
            gutter={{ base: 'large' }}
            css={[
              !headings.length && {
                justifyContent: 'center',
              },
            ]}
          >
            {headings.length ? (
              <RowColumn
                size={{ medium: 3 }}
                css={{
                  [theme.mq.small__max]: {
                    display: 'none',
                  },
                }}
              >
                <TableOfContents tree={tableOfContents} />
              </RowColumn>
            ) : null}
            <RowColumn size={{ medium: 9 }}>
              <header css={{ marginBottom: theme.space.large }}>
                <div
                  css={{
                    position: 'relative',
                    textAlign: 'center',

                    [theme.mq.small__max]: {
                      marginLeft: -theme.space.base,
                      marginRight: -theme.space.base,
                    },
                  }}
                />
              </header>
              <MDXRenderer>{body}</MDXRenderer>
            </RowColumn>
          </Row>
        </Container>
      </Section>
      <FilteredContent
        heading={pageData.awsDiscover.heading}
        filters="topics:AWS"
        hideSeparator
        css={{ paddingBottom: `${theme.space.xlarge}px !important` }}
      />
    </BaseLayout>
  )
}

AWS.propTypes = {
  data: PropTypes.object.isRequired,
}

export default AWS

export const pageQuery = graphql`
  query AWSPageData {
    file(name: { eq: "aws" }, absolutePath: { regex: "/src/data/" }) {
      childMdx {
        tableOfContents(maxDepth: 3)
        frontmatter {
          title
          description
          socialMediaImage
          hero {
            blurb
            heading
            marketplace {
              label
              url
            }
          }
          awsDiscover {
            heading
          }
        }
        body
        headings {
          value
          depth
        }
      }
    }
  }
`
